<template>
  <div class="entry-grid">
    <div class="field-cell">
      <label class="entry-label" for="chat-search">Barcode / Scan</label>
      <div class="scan-wrap">
        <i class="fa fa-barcode scan-icon"></i>
        <input type="text" class="form-control scan-field"
          v-model="barcodeText"
          id="chat-search"
          placeholder="Scan barcode"
          aria-label="Search..."
          aria-describedby="chat-search"
          @keydown.enter="onScanBarcode"
          ref="productScanField"
        />
      </div>
    </div>
    <div class="field-cell">
      <label class="entry-label">Product</label>
      <AsyncSelect
        placeholder="Search product name / SKU"
        v-model="selectedProduct"
        label="text"
        :api-service="fetchProductDropdownList"
        :format-label="option => option.text"
        :option-selected="onSelectNote"
        :additional-query="additionalProductQuery"
        :min-char-to-search="2"
        skip-api-on-open
        ref="productField"
      />
    </div>
    <div class="field-cell">
      <label class="entry-label" for="quantity">Qty</label>
      <input
        ref="quantityField"
        id="quantity"
        name="quantity"
        type="number"
        class="form-control qty-input"
        v-model="formData.quantity"
        placeholder="0"
        @keydown.enter="handleProductSelection"
      />
    </div>

    <Loader v-if="isLoader"/>
  </div>
</template>


<script setup>
import {inject, onMounted, ref, watch, toRef, nextTick} from 'vue'
import {useRoute} from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import useDate from "@/services/utils/day";
import Loader from "@/components/atom/LoaderComponent";
import handleDropdowns from "@/services/modules/dropdowns";

const props = defineProps({
  tableItems: {
    type: Array,
    default: () => []
  },
  additionalProductQuery: {
    type: Object,
    default: () => ({})
  }
})

const $route = useRoute();
const date = useDate();
const isLoader = ref(false);
const quantityField = ref(null)
const productField = ref(null)
const productScanField = ref(null);
const companyId = $route.params.companyId
const companyQuery = `?company_id=${companyId}`;
const formData = inject('formData');
const showError = inject("showError")
const barcodeText = ref(null);
const selectedProduct = ref(null);
const productId = ref(null);
const tableItemsRef = toRef(props, 'tableItems');

const isFromProductScan = ref(false);

const {fetchProductById} = handlePurchase()
const { fetchProductDropdownList } = handleDropdowns();

const onSelectNote = (note) => {
  productId.value = note.id
}

const focusProductSelect = () => {
  if (productField.value) {
    productField.value.clear();
    productField.value.dropdownRef.searchEl.focus()
  }
}

const focusProductScan = () => {
  if (productScanField.value) {
    productScanField.value.focus();
  }
  isFromProductScan.value = false;
}

const focusMethod = () => {
  focusProductSelect();
  if (isFromProductScan.value) {
    focusProductScan();
  }
}

const handleProductSelection = async () => {
  if (!productId.value) {
    showError("Please select product first!");
    focusMethod();
    return;
  }

  if (!formData.value.quantity) {
    showError("Please enter valid quantity first!");
    return;
  }

  let quantity = Number(formData.value.quantity)

  if (!Number.isInteger(quantity)) {
    showError("The quantity can only be a number");
    formData.value.quantity = parseInt(formData.value.quantity)
    return;
  }

  const productResp = await fetchProductById(productId.value, `${companyQuery}&location_id=${formData.value.location_id}`);

  if (!productResp.data) {
    showError("Product not found");
    focusMethod();
    return;
  }

  const selected = productResp.data;
  let alreadyExist = false;

  let product = {
    name: selected.name
  }

  props.tableItems.map((item) => {
    if (item.product_id === selected.id) {
      alreadyExist = true;
      item.quantity += quantity;
    }
  })

  if (!alreadyExist) {
    props.tableItems.push({
      product_id: selected.id,
      name: selected.name,
      stock_in_hand: selected.stock_in_hand,
      description: '',
      quantity: quantity,
      rate: selected.sales_price,
      purchase_rate: selected.purchase_rate,
      discount: 0,
      vat: 0,
      sub_total: 0,
      discount_amount: 0,
      discount_percent: 0,
      vat_amount: 0,
      total: 0,
      is_edit: false,
      batch_details: [],
      product: product
    })
  }

  formData.value.quantity = null;
  productId.value = null;
  selectedProduct.value = null;
  focusMethod();
}
const generateDateAndInvoice = () => {
  formData.value.date = date.currentDate();
}

const resetData = () => {
  generateDateAndInvoice();
  productId.value = null;
  tableItemsRef.value = [];
  productField.value.clear();
  barcodeText.value = null;
  focusProductScan();
}

const onScanBarcode = async () => {
  if (! barcodeText.value) return;

  let query = `${companyQuery}&with_desc_stock=1&barcode_sku=${barcodeText.value}&location_id=${formData.value.location_id}`;

  try {
    isLoader.value = true;
    const response = await fetchProductDropdownList(query);

    if (! response.status) {
      showError("No product found with this barcode");
      barcodeText.value = null;
      isLoader.value = false;
      return;
    }

    const foundProduct = response.data[0];
    productId.value = foundProduct.id;
    selectedProduct.value = foundProduct;
    barcodeText.value = null;
    formData.value.quantity = 1;
    isFromProductScan.value = true;
    await handleProductSelection();
  } catch (error) {
    showError("Failed to fetch product by barcode");
  } finally {
    isLoader.value = false;
  }
}

onMounted(() => {
  generateDateAndInvoice();
  nextTick(() => {
    focusProductScan();
  });
})

watch(productId, () => {
  if (!productId.value) {
    return;
  }

  if (quantityField.value) {
    quantityField.value.focus();
  }
})

defineExpose({
  resetData
})

</script>

<style scoped>
.entry-grid {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) 90px;
  gap: 10px;
  align-items: start;
}
.field-cell {
  min-width: 0;
}
.entry-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #667085;
  margin-bottom: 4px;
  white-space: nowrap;
}
.entry-grid .form-control {
  height: 42px;
  font-size: 15px;
  border-radius: 8px;
}
.scan-wrap {
  position: relative;
}
.scan-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #b58a1d;
  pointer-events: none;
}
.scan-field {
  background: #fffbe6;
  border-color: #f2d675;
  font-weight: 600;
  padding-left: 34px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.scan-field:focus {
  background: #fffbe6;
  border-color: #d4a72c;
  box-shadow: 0 0 0 3px rgba(212, 167, 44, 0.15);
}
.qty-input {
  text-align: center;
  font-weight: 600;
}
.entry-grid :deep(.vs__dropdown-toggle) {
  min-height: 42px;
  border-radius: 8px;
  padding-bottom: 0;
}
.entry-grid :deep(.vs__selected-options) {
  flex-wrap: nowrap;
  overflow: hidden;
}
.entry-grid :deep(.vs__selected) {
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.entry-grid :deep(.vs__search) {
  font-size: 15px;
}
@media (max-width: 992px) {
  .entry-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>